import { Chart } from '@frontend/chart/types';

export type PeriodComparisonDirection = 'up' | 'down' | 'flat';

export interface PeriodComparisonMetric {
    /** Value for the most recent period bin. */
    value: number;
    /** Value for the prior period bin, or null when only one bin exists. */
    previous: number | null;
    percentChange: number | null;
    direction: PeriodComparisonDirection;
}

const lastTwoPoints = (chart: Chart): number[] => {
    const points = chart.series[0]?.data ?? [];
    return points.slice(-2).map((point) => point.value);
};

export type PeriodComparisonMode = 'relative' | 'absolute';

export const toPeriodComparisonMetric = (chart: Chart, mode: PeriodComparisonMode = 'relative'): PeriodComparisonMetric | null => {
    const recentValues = lastTwoPoints(chart);

    if (recentValues.length === 0) {
        return null;
    }

    const value = recentValues[recentValues.length - 1];

    if (recentValues.length < 2) {
        return { value, previous: null, percentChange: null, direction: 'flat' };
    }

    const previous = recentValues[0];
    let percentChange: number | null;

    if (mode === 'absolute') {
        percentChange = Math.round(value - previous);
    } else if (previous === 0) {
        percentChange = null;
    } else {
        percentChange = Math.round(((value - previous) / previous) * 100);
    }

    if (percentChange === null) {
        return { value, previous, percentChange: null, direction: 'flat' };
    }

    if (percentChange > 0) {
        return { value, previous, percentChange, direction: 'up' };
    }

    if (percentChange < 0) {
        return { value, previous, percentChange: Math.abs(percentChange), direction: 'down' };
    }

    return { value, previous, percentChange: 0, direction: 'flat' };
};
